import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getGateSchedule } from 'services/clients/gateSchedule';
import { useRegion } from 'common/hooks/region';
import { PageHeader } from 'layouts/Generic/page-header';
import { Page } from 'components/Page';
import { Divider } from '@mui/material';
import GateScheduleTable from './GateScheduleTable';
import GateScheduleService from './services/gate-schedule.service';
import { tableIds } from './constants/gate-schedule.constants';

/** How often to refetch the gate schedule, in milliseconds */
const REFETCH_INTERVAL = 0.5 * 60 * 1000; // Every 0.5 minute(s)

export const GateSchedule: React.FC = () => {
  const region = useRegion();

  // Run our react-query's in parallel to retrieve this and next week
  // Note that these queries will automatically refresh on window refocus or on the refetch interval
  const currentWeekGateSchedule = useQuery(
    ['gateSchedule', region.code, 0],
    () => getGateSchedule(0, region.code),
    {
      refetchInterval: REFETCH_INTERVAL,
    }
  );
  const nextWeekGateSchedule = useQuery(
    ['gateSchedule', region.code, 1],
    () => getGateSchedule(1, region.code),
    {
      refetchInterval: REFETCH_INTERVAL,
    }
  );

  const handleDownloadClick = React.useCallback((isNextWeek?: boolean) => {
    GateScheduleService.saveGateScheduleAsPDF(isNextWeek);
  }, []);

  return (
    <Page className="p-16 py-8 space-y-8">
      <PageHeader title="Gate Schedules" />
      <Divider />
      <div className="gate-schedule">
        <GateScheduleTable
          tableId={tableIds.currentWeek}
          tableData={currentWeekGateSchedule.data}
          isFetching={currentWeekGateSchedule.isFetching}
          error={currentWeekGateSchedule.error}
          onDownloadClick={handleDownloadClick}
        />
        <GateScheduleTable
          isNextWeek
          tableId={tableIds.nextWeek}
          tableData={nextWeekGateSchedule.data}
          isFetching={nextWeekGateSchedule.isFetching}
          error={nextWeekGateSchedule.error}
          onDownloadClick={handleDownloadClick}
        />
      </div>
    </Page>
  );
};
GateSchedule.displayName = 'GateSchedule';
